// La subida por trozos, del lado de la pantalla. Sin React: se prueba sola.
//
// Menos de 5 MB va de una vez (/subir). Más, por trozos: se abre la subida,
// se manda cada trozo, y si algo falla se pregunta al servidor qué le
// falta y se manda solo eso, hasta tres rondas. Al final, /completar.
import { api, ErrorApi } from '../api';

export const TROZO = 5 * 1024 * 1024;
export const DE_UNA_VEZ = 5 * 1024 * 1024;

export function planificar(bytes) {
  return { porTrozos: bytes > DE_UNA_VEZ, trozos: Math.max(1, Math.ceil(bytes / TROZO)) };
}

async function mandarTrozo(id, numero, blob, { fetchFn = fetch } = {}) {
  const r = await fetchFn(`/api/archivos/subidas/${id}/trozos/${numero}`, { method: 'PUT', body: blob });
  if (!r.ok) {
    let detalle = `Error ${r.status}`;
    try { detalle = (await r.json()).detalle || detalle; } catch { /* sin cuerpo */ }
    throw new ErrorApi(detalle, r.status);
  }
  return r.json();
}

/**
 * Sube `archivo` (un File/Blob con `name`). Llama `alAvanzar(porCiento)`.
 * Devuelve lo que contestó el servidor (la ficha del archivo).
 */
export async function subirArchivo(archivo, { idCarpeta = null, alAvanzar = () => {}, fetchFn = fetch, rondas = 3 } = {}) {
  const plan = planificar(archivo.size);
  if (!plan.porTrozos) {
    const params = idCarpeta != null ? `?id_carpeta=${idCarpeta}` : '';
    alAvanzar(5);
    const r = await api.subir(`/archivos/subir${params}`, archivo);
    alAvanzar(100);
    return r;
  }
  const abierta = await api.post('/archivos/subidas', { nombre: archivo.name, bytes_total: archivo.size, id_carpeta: idCarpeta });
  let faltan = Array.from({ length: abierta.trozos_total }, (_, i) => i);
  let ultimoError = null;
  for (let ronda = 0; ronda < rondas && faltan.length; ronda += 1) {
    for (const n of faltan) {
      const inicio = n * abierta.trozo_bytes;
      try {
        const r = await mandarTrozo(abierta.id, n, archivo.slice(inicio, inicio + abierta.trozo_bytes), { fetchFn });
        alAvanzar(Math.min(99, Math.round(((abierta.trozos_total - r.faltan) / abierta.trozos_total) * 100)));
      } catch (e) {
        ultimoError = e;
        if (e instanceof ErrorApi && e.status >= 400 && e.status < 500 && e.status !== 408) throw e;
        break;                           // se cayó la red: a preguntar qué falta
      }
    }
    const estado = await api.get(`/archivos/subidas/${abierta.id}`);
    faltan = estado.faltan;
  }
  if (faltan.length) {
    throw ultimoError || new ErrorApi('No se pudo terminar la subida: la red se cortó varias veces. Vuelve a intentarlo.', 0);
  }
  const listo = await api.post(`/archivos/subidas/${abierta.id}/completar`, {});
  alAvanzar(100);
  return listo;
}

/** Varios a la vez, en orden, sin que uno malo pare a los demás. */
export async function subirVarios(archivos, { idCarpeta = null, alCambiar = () => {} } = {}) {
  const estados = archivos.map((a) => ({ nombre: a.name, bytes: a.size, porCiento: 0, estado: 'EN_COLA', error: null, resultado: null }));
  alCambiar([...estados]);
  for (let i = 0; i < archivos.length; i += 1) {
    estados[i].estado = 'SUBIENDO';
    alCambiar([...estados]);
    try {
      estados[i].resultado = await subirArchivo(archivos[i], {
        idCarpeta,
        alAvanzar: (p) => { estados[i].porCiento = p; alCambiar([...estados]); },
      });
      estados[i].estado = 'LISTO';
    } catch (e) {
      estados[i].estado = 'ERROR';
      estados[i].error = e instanceof ErrorApi ? e.message : 'No se pudo subir. Revisa la conexión y vuelve a intentarlo.';
    }
    alCambiar([...estados]);
  }
  return estados;
}
